/**
 * Application Helper Functions
 * Centralized utility functions for the translation admin tool
 */

/**
 * Generate a unique ID with fallback for older browsers
 * Uses crypto.randomUUID() in modern browsers, falls back to timestamp + random for older browsers
 * @returns A unique identifier string
 */
export const generateUniqueId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Cookie Helper Functions
 */

/**
 * Get a cookie value by name
 * @param name - The name of the cookie to retrieve
 * @returns The cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(';').shift() || null;
  return null;
}

/**
 * Set a cookie with secure defaults
 * Uses SameSite=Strict for better CSRF protection , but doesn't work for local login...
 * @param name - The name of the cookie
 * @param value - The value to store
 * @param days - Optional number of days until expiration
 */
export function setCookie(name: string, value: string, days?: number): void {
  let expires = '';
  if (days) {
    expires = `; Expires=${new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()}`;
  }
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${value}${expires}; Path=/; SameSite=Lax${secure}`;
}

/**
 * Create or retrieve session cookies for tracking
 * Initializes bsw-sessionId and bsw-correlationId if they don't exist
 */
export function createSessionCookies(): void {
  let sessionId = getCookie('bsw-sessionId');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    setCookie('bsw-sessionId', sessionId);
  }
  let correlationId = getCookie('bsw-correlationId');
  if (!correlationId) {
    correlationId = crypto.randomUUID();
    setCookie('bsw-correlationId', correlationId);
  }
}

/**
 * Get or create a session ID
 * @returns The session ID string
 */
export const getSessionId = (): string => {
  let sessionId = getCookie('bsw-sessionId');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    setCookie('bsw-sessionId', sessionId);
  }
  return sessionId;
};

/**
 * Get or create a correlation ID
 * @returns The correlation ID string
 */
export const getCorrelationId = (): string => {
  let correlationId = getCookie('bsw-correlationId');
  if (!correlationId) {
    correlationId = crypto.randomUUID();
    setCookie('bsw-correlationId', correlationId);
  }
  return correlationId;
};
